import api from './api'

/**
 * Birleşik Onay Merkezi (Approval Hub) API istemcisi.
 *
 * Tek kuyruk: teklif onayları + İK izin/görev talepleri + masraf fişleri.
 * Backend yalnız kullanıcının GERÇEKTEN onaylayabileceği kayıtları döner —
 * arayüz ayrıca bir yetki filtresi uygulamaz (yetki tek yerde: backend).
 *
 * Kayıt anahtarı modüller arası benzersizdir: `"quote:42"`, `"leave:7"` —
 * id'ler modüller arasında çakıştığı için toplu işlemde bu anahtar kullanılır.
 */
export const approvalService = {
  /** Onay bekleyenler + özet + facet'ler. */
  getPending: async (company, params = {}) => {
    const r = await api.get('/approvals/pending', { params: { company, ...params } })
    return r.data
  },

  /** Header rozeti — yalnız sayaç/tutar (liste gövdesi taşınmaz). */
  getCount: async (company) => {
    // silentNetToast: arka plan poll timeout'u kırmızı toast basmasın
    // (timeout 10 sn — 30 sn global yerine; backend artık canias açmaz).
    const r = await api.get('/approvals/count', {
      params: { company },
      timeout: 10000,
      silentNetToast: true,
    })
    return r.data
  },

  /**
   * Tekil veya toplu onay/red.
   * @param {string[]} approvalIds ["quote:42", "expense:9"]
   * @param {'approve'|'reject'} action
   * @param {string} [rejectReason] redde ZORUNLU
   */
  act: async (company, approvalIds, action, rejectReason) => {
    const r = await api.post('/approvals/action',
      { approval_ids: approvalIds, action, reject_reason: rejectReason || undefined },
      { params: { company } })
    return r.data
  },
}

/** Modül rozeti renkleri — Teklif mavi, İzin yeşil, Görev mor, Masraf turuncu. */
export const MODULE_STYLE = {
  quote: { label: 'Teklif', chip: 'bg-blue-50 text-blue-700 border-blue-100', dot: 'bg-blue-500' },
  // canias imza kuyruğu — aynı rozet (kullanıcı için ikisi de "teklif"), ayrım `source` kolonunda
  quote_sig: { label: 'Teklif', chip: 'bg-blue-50 text-blue-700 border-blue-100', dot: 'bg-blue-500' },
  leave: { label: 'İzin', chip: 'bg-emerald-50 text-emerald-700 border-emerald-100', dot: 'bg-emerald-500' },
  task: { label: 'Görev', chip: 'bg-violet-50 text-violet-700 border-violet-100', dot: 'bg-violet-500' },
  duty: { label: 'Dış Görev', chip: 'bg-sky-50 text-sky-700 border-sky-100', dot: 'bg-sky-500' },
  pdks_fix: { label: 'Puantaj', chip: 'bg-amber-50 text-amber-700 border-amber-100', dot: 'bg-amber-500' },
  expense: { label: 'Masraf', chip: 'bg-orange-50 text-orange-700 border-orange-100', dot: 'bg-orange-500' },
  advance: { label: 'Avans', chip: 'bg-lime-50 text-lime-700 border-lime-100', dot: 'bg-lime-500' },
  payreq: { label: 'Ödeme Talebi', chip: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100', dot: 'bg-fuchsia-500' },
  return: { label: 'İade', chip: 'bg-red-50 text-red-600 border-red-100', dot: 'bg-red-500' },
  bom_release: { label: 'Reçete Kapanışı', chip: 'bg-indigo-50 text-indigo-700 border-indigo-100', dot: 'bg-indigo-500' },
  bom_change: { label: 'Reçete Değişikliği', chip: 'bg-amber-50 text-amber-700 border-amber-100', dot: 'bg-amber-500' },
  wo_change: { label: 'İş Emri Malzemesi', chip: 'bg-orange-50 text-orange-700 border-orange-100', dot: 'bg-orange-500' },
  bom_draft: { label: 'Reçete Taslağı', chip: 'bg-violet-50 text-violet-700 border-violet-100', dot: 'bg-violet-500' },
  product: { label: 'Ürün', chip: 'bg-teal-50 text-teal-700 border-teal-100', dot: 'bg-teal-500' },
  partner_order: { label: 'İş Ortağı Siparişi', chip: 'bg-cyan-50 text-cyan-700 border-cyan-100', dot: 'bg-cyan-500' },
  purchase: { label: 'Satınalma', chip: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-500' },
  paymcond: { label: 'Cari Vade', chip: 'bg-yellow-50 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' },
  order_risk: { label: 'Sipariş Risk', chip: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  credit_limit: { label: 'Kredi Limiti', chip: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
}

export const URGENCY_STYLE = {
  high: { label: 'Acil', chip: 'bg-red-50 text-red-600 border-red-100' },
  normal: { label: 'Normal', chip: 'bg-amber-50 text-amber-700 border-amber-100' },
  low: { label: 'Yeni', chip: 'bg-gray-50 text-gray-500 border-gray-100' },
}

/** "2 gün önce" — kaç gündür beklediği (kuyruk sıralamasının görünen karşılığı). */
export function waitingLabel(ageDays) {
  if (ageDays == null) return '—'
  if (ageDays < 1) {
    const h = Math.max(Math.round(ageDays * 24), 1)
    return `${h} saattir`
  }
  return `${Math.floor(ageDays)} gündür`
}

/** API `target_url` boşsa modül+id'den deeplink. */
export function resolveApprovalUrl(it) {
  if (it?.target_url) return it.target_url
  const id = it?.id
  switch (it?.module) {
    case 'quote':
      return id != null ? `/quotes/${id}` : '/approvals'
    case 'quote_sig': {
      const parts = String(id || '').split('|')
      if (parts.length >= 3 && parts[1] && parts[2]) {
        return `/sales/approvals/${encodeURIComponent(parts[1])}/${encodeURIComponent(parts[2])}`
          + (parts[0] ? `?company=${encodeURIComponent(parts[0])}` : '')
      }
      return '/sales/approvals'
    }
    case 'product':
      return id != null ? `/stock/product-approvals?request=${id}` : '/stock/product-approvals'
    case 'expense':
      return id != null ? `/hcm/expense-approvals?claim=${id}` : '/hcm/expense-approvals'
    case 'advance':
      return id != null ? `/hcm/advance-approvals?advance=${id}` : '/hcm/advance-approvals'
    case 'payreq':
      return id != null ? `/finance/payment-requests?request=${id}` : '/finance/payment-requests?tab=approve'
    case 'duty':
      return id != null ? `/hcm/duties?duty=${id}` : '/hcm/duties?tab=approvals'
    case 'pdks_fix':
      return '/hcm/timesheet?tab=team'
    case 'leave':
    case 'task':
      return id != null ? `/hcm/leave-approvals?request=${id}` : '/hcm/leave-approvals'
    case 'return':
      return id != null ? `/returns/${id}` : '/returns'
    case 'partner_order':
      return id != null ? `/partners/orders?order=${id}` : '/partners/orders'
    case 'purchase': {
      // canias belge anahtarı: firma|ordertype|ordernum
      const parts = String(id || '').split('|')
      if (parts.length >= 3 && parts[2]) {
        const q = new URLSearchParams({ company: parts[0] || '', doctype: parts[1] || '' })
        return `/purchase/approvals/${encodeURIComponent(parts[2])}?${q.toString()}`
      }
      return '/purchase/approvals'
    }
    default:
      return '/approvals'
  }
}
